import React, { useEffect } from "react";
import {
  SACard,
  SAButton,
  SAIcon,
  SAIcons,
  SAIconSize,
  SAModal,
  SAText,
  SAUXTheme
} from '@saux/design-system-react'
import styled from 'styled-components';

const mobileWidth = 600;

type CancelAlertProps = {
  onClickStay: Function;
  onClickLeave: Function;
  title?: string;
};

type StyleProps = {
  error?: boolean;
  theme?: SAUXTheme;
};

const Modal = styled(SAModal)`
  display: flex;
  top: 50%;
  left: 50%;
  justify-content: center;
  align-items: center;
  overflow: auto;
`;

const CancelAlert = styled(SACard)`
  ${({ theme }: StyleProps) => {
    return `
      display: flex;
      flex-direction: column;
      width: 600px;
      overflow: auto;
      margin: auto;

      section {
        display: flex;
        flex-direction: column;
        flex-grow: 1;
      }

      @media only screen and (max-width: ${mobileWidth}px) {
        flex-grow: 1;
        width: auto;
        width: 100%;
        height: 100%;
        border-radius: 0;

        section {
          padding: ${theme?.size.spacer.large} ${theme?.size.spacer.medium};
        }
      }
    `;
  }};
`;

const ModalHeader = styled.div`
  padding-bottom: 45px;
`;

const ModalTitle = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  height: 30px;

  button:last-child {
    display: none;

    &:focus {
      outline: auto;
    } 
  }

  @media only screen and (max-width: ${mobileWidth}px) {
    button:last-child {
      display: flex;
    }
  }
`;

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  
  span {
    font-size: 20px;
    line-height: 26px;
  }
`;

const ModalFooter: any = styled.div`
${({ theme }: StyleProps) => {
  return `
    display: flex;
    flex-direction: row;
    flex-grow: 1;
    justify-content: flex-end;
    align-items: flex-end;
    padding-top: 60px;
    
    button {
      font-weight: ${theme?.font.primary.weight.normal};
      margin-left: ${theme?.size.spacer.medium};

      &:first-child {
        margin-left: 0;
      }

      &:last-child {
        font-weight: ${theme?.font.primary.weight.bold};
      }
    }

    @media only screen and (max-width: ${mobileWidth}px) {
      justify-content: center;
      height: 100%;
    }
  `;
}};
`;

export default function ModalCancelAlert({ onClickStay, onClickLeave, title }: CancelAlertProps) {
  function handleClickCancelAlertModal(event: React.MouseEvent) {
    event.stopPropagation();

    // get the card element inside the modal
    const cardElement = document.querySelector('#modal > div > article > section');

    // close modal when clicking outside of the card element
    if (!(cardElement instanceof Element && cardElement.contains(event.target as Node))) {
      onClickStay(event);
    }
  }

  function handleClickStay(event: React.MouseEvent) {
    event.stopPropagation();
    onClickStay(event);
  }

  function handleClickLeave(event: React.MouseEvent) {
    event.stopPropagation();
    onClickLeave(event);
  }

  useEffect(() => {
    function focusDefault() {
      const modal = document.querySelector('#modalcancelalert') as HTMLElement;
      if (modal) {
        const element = modal.querySelector('button.sa-button:nth-child(2)') as HTMLElement;
        if (element) {
          element.focus();
        }
      } 
    }

    function focusModalCancelAlert(e: any) {
      const modal = document.querySelector('#modalcancelalert') as HTMLElement;
      const root = document.querySelector('#root') as HTMLElement;
      if (modal && root) {
        if ((e.type === 'focusout' && root.contains(e.relatedTarget)) || (e.type === 'focusin' && root.contains(e.target))) {
          let element = null;
          if (document.documentElement.clientWidth <= mobileWidth) {
            element = modal.querySelector('button') as HTMLElement;
          } else {
            element = modal.querySelector('button.sa-button') as HTMLElement;
          }
          if (element) {  
            element.focus();
          }
        }
      }
    }

    focusDefault();
    document.addEventListener('focusin', focusModalCancelAlert);
    document.addEventListener('focusout', focusModalCancelAlert);

    return () => {
      document.removeEventListener('focusin', focusModalCancelAlert);
      document.removeEventListener('focusout', focusModalCancelAlert);
    }
  }, []);
  
  return (
    <Modal id="modalcancelalert" onClickHandler={handleClickCancelAlertModal}>
      <CancelAlert variant="minimal">
        <ModalHeader>
          <ModalTitle>
            <SAText type="heading-2" text={title ? title : "Cancel Submission"} />
            <SAIcon className="closemodal" icon={SAIcons.x} size={SAIconSize.medium} colorVariant="dark" onClick={handleClickStay} />
          </ModalTitle>
        </ModalHeader>
        <ModalBody>
          <SAText type="standard" text="Are you sure you want to cancel? Changes will not be saved." />
        </ModalBody>
        <ModalFooter>
          <SAButton fullWidthUnder={mobileWidth} label="Leave" onClick={handleClickLeave} variant="link-large" color="text" textTransform="uppercase" />
          <SAButton fullWidthUnder={mobileWidth} label="Stay" onClick={handleClickStay} variant="large" color="secondary" textTransform="uppercase" />
        </ModalFooter>
      </CancelAlert>
    </Modal>
  );
}
